import type {
  BookingDeposit,
  InvoiceSummary,
  PaymentCheckout,
  PaymentLink,
  PaymentLinkStatusResponse,
  PaymentProviderEnvironment,
  PaymentProviderName,
  PaymentSummary,
  PaymentTransactionSummary,
  RefundSummary
} from "@kuroo/contracts";

export type SdkPaymentContext = {
  request: <T>(path: string, init?: RequestInit) => Promise<T>;
};

export function payments(context: SdkPaymentContext, filters: { from?: string; to?: string; status?: string } = {}): Promise<PaymentSummary[]> {
  const query = new URLSearchParams();
  if (filters.from) query.set("from", filters.from);
  if (filters.to) query.set("to", filters.to);
  if (filters.status) query.set("status", filters.status);
  return context.request<PaymentSummary[]>(`/api/v1/payments${query.size ? `?${query}` : ""}`);
}

export function createPaymentCheckout(context: SdkPaymentContext, input: { provider?: PaymentProviderName; integrationAccountId?: string; sourceId?: string; bookingId?: string; invoiceId?: string; recurringSeriesId?: string; paymentLinkToken?: string; environment?: PaymentProviderEnvironment; returnUrls?: { success?: string; pending?: string; failure?: string }; idempotencyKey: string }): Promise<PaymentCheckout> {
  return context.request<PaymentCheckout>("/api/v1/payments/checkout", { method: "POST", body: JSON.stringify(input), headers: { "Idempotency-Key": input.idempotencyKey } });
}

export function payment(context: SdkPaymentContext, paymentId: string): Promise<{ payment: PaymentSummary; transactions: PaymentTransactionSummary[]; refunds: RefundSummary[] }> {
  return context.request<{ payment: PaymentSummary; transactions: PaymentTransactionSummary[]; refunds: RefundSummary[] }>(`/api/v1/payments/${encodeURIComponent(paymentId)}`);
}

export function updatePayment(context: SdkPaymentContext, paymentId: string, input: { status?: "pending" | "authorized" | "paid" | "partially_refunded" | "refunded" | "failed" | "cancelled"; statusDetail?: string | null }): Promise<{ id: string; status: string; statusDetail: string | null }> {
  return context.request<{ id: string; status: string; statusDetail: string | null }>(`/api/v1/payments/${encodeURIComponent(paymentId)}`, { method: "PATCH", body: JSON.stringify(input) });
}

export function deletePayment(context: SdkPaymentContext, paymentId: string): Promise<{ id: string; status: "cancelled"; deleted: boolean }> {
  return context.request<{ id: string; status: "cancelled"; deleted: boolean }>(`/api/v1/payments/${encodeURIComponent(paymentId)}`, { method: "DELETE" });
}

export function calculatePaymentAmount(context: SdkPaymentContext, input: { subtotalCents?: number; amountCents?: number; currency?: string; couponCode?: string }): Promise<{ subtotalCents: number; discountCents: number; totalCents: number; currency: string }> {
  return context.request<{ subtotalCents: number; discountCents: number; totalCents: number; currency: string }>("/api/v1/payments/amount", { method: "POST", body: JSON.stringify(input) });
}

export function paymentTransaction(context: SdkPaymentContext, transactionId: string): Promise<{ id: string; paymentId: string; kind: string; amountCents: number; providerTransactionId?: string | null; status: string; currency: string; createdAt: string }> {
  return context.request<{ id: string; paymentId: string; kind: string; amountCents: number; providerTransactionId?: string | null; status: string; currency: string; createdAt: string }>(`/api/v1/payments/transactions/${encodeURIComponent(transactionId)}`);
}

export function refundPaymentById(context: SdkPaymentContext, paymentId: string, input: { amountCents: number; idempotencyKey: string; reason?: string; method?: "mercadopago" | "cash"; occurrenceIds?: string[]; overrideReason?: string }): Promise<RefundSummary> {
  return context.request<RefundSummary>(`/api/v1/payments/${encodeURIComponent(paymentId)}/refund`, { method: "POST", body: JSON.stringify({ ...input, paymentId }), headers: { "Idempotency-Key": input.idempotencyKey } });
}

export function approveRefund(context: SdkPaymentContext, refundId: string, input: { method: "mercadopago" | "cash"; amountCents?: number; overrideReason?: string; idempotencyKey: string }): Promise<RefundSummary> {
  return context.request<RefundSummary>(`/api/v1/payments/refunds/${encodeURIComponent(refundId)}/approve`, { method: "POST", body: JSON.stringify(input), headers: { "Idempotency-Key": input.idempotencyKey } });
}

export function rejectRefund(context: SdkPaymentContext, refundId: string, reason: string): Promise<RefundSummary> {
  return context.request<RefundSummary>(`/api/v1/payments/refunds/${encodeURIComponent(refundId)}/reject`, { method: "POST", body: JSON.stringify({ reason }) });
}

export function completeCashRefund(context: SdkPaymentContext, refundId: string, cashReceiptReference: string, idempotencyKey = `cash-delivery:${refundId}:${cashReceiptReference}`): Promise<RefundSummary> {
  return context.request<RefundSummary>(`/api/v1/payments/refunds/${encodeURIComponent(refundId)}/cash-delivered`, { method: "POST", body: JSON.stringify({ cashReceiptReference, idempotencyKey }), headers: { "Idempotency-Key": idempotencyKey } });
}

export function retryRefund(context: SdkPaymentContext, refundId: string, idempotencyKey: string): Promise<RefundSummary> {
  return context.request<RefundSummary>(`/api/v1/payments/refunds/${encodeURIComponent(refundId)}/retry`, { method: "POST", body: JSON.stringify({ idempotencyKey }), headers: { "Idempotency-Key": idempotencyKey } });
}

export function refunds(context: SdkPaymentContext, filters: { status?: string } = {}): Promise<RefundSummary[]> {
  const query = new URLSearchParams();
  if (filters.status) query.set("status", filters.status);
  return context.request<RefundSummary[]>(`/api/v1/payments/refunds${query.size ? `?${query}` : ""}`);
}

export function capturePayment(context: SdkPaymentContext, paymentId: string): Promise<{ paymentId: string; provider: string; providerPaymentId: string; status: string; statusDetail?: string | null; amountCents: number; currency: string }> {
  return context.request<{ paymentId: string; provider: string; providerPaymentId: string; status: string; statusDetail?: string | null; amountCents: number; currency: string }>(`/api/v1/payments/${encodeURIComponent(paymentId)}/capture`, { method: "POST", body: "{}" });
}

export function paymentTransactions(context: SdkPaymentContext, paymentId: string): Promise<{ payment: PaymentSummary; transactions: PaymentTransactionSummary[]; refunds: RefundSummary[] }> {
  return context.request<{ payment: PaymentSummary; transactions: PaymentTransactionSummary[]; refunds: RefundSummary[] }>(`/api/v1/payments/${encodeURIComponent(paymentId)}/transactions`);
}

export function invoices(context: SdkPaymentContext): Promise<InvoiceSummary[]> {
  return context.request<InvoiceSummary[]>("/api/v1/invoices");
}

export function invoice(context: SdkPaymentContext, invoiceId: string): Promise<InvoiceSummary> {
  return context.request<InvoiceSummary>(`/api/v1/invoices/${encodeURIComponent(invoiceId)}`);
}

export function createInvoice(context: SdkPaymentContext, input: { bookingId: string; taxRateBps?: number; number?: string; dueAt?: string }): Promise<InvoiceSummary> {
  return context.request<InvoiceSummary>("/api/v1/invoices", { method: "POST", body: JSON.stringify(input), headers: { "Idempotency-Key": `invoice-${input.bookingId}` } });
}

export function voidInvoice(context: SdkPaymentContext, invoiceId: string): Promise<{ id: string; status: "void" }> {
  return context.request<{ id: string; status: "void" }>(`/api/v1/invoices/${encodeURIComponent(invoiceId)}/void`, { method: "POST", body: "{}" });
}

export function bookingDeposit(context: SdkPaymentContext, bookingId: string): Promise<BookingDeposit | null> {
  return context.request<BookingDeposit | null>(`/api/v1/bookings/${encodeURIComponent(bookingId)}/deposit`);
}

export function createBookingDeposit(context: SdkPaymentContext, input: { bookingId: string; amountCents: number; dueAt?: string }): Promise<BookingDeposit> {
  return context.request<BookingDeposit>(`/api/v1/bookings/${encodeURIComponent(input.bookingId)}/deposit`, { method: "POST", body: JSON.stringify(input) });
}

export function createPaymentLink(context: SdkPaymentContext, input: { bookingId?: string; invoiceId?: string; amountCents?: number; ttlSeconds?: number }): Promise<PaymentLink> {
  return context.request<PaymentLink>("/api/v1/payments/links", { method: "POST", body: JSON.stringify(input) });
}

export function paymentLink(context: SdkPaymentContext, token: string): Promise<PaymentLink> {
  return context.request<PaymentLink>(`/api/v1/payments/links/${encodeURIComponent(token)}`);
}

export function paymentLinkStatus(context: SdkPaymentContext, token: string): Promise<PaymentLinkStatusResponse> {
  return context.request<PaymentLinkStatusResponse>(`/api/v1/payments/links/${encodeURIComponent(token)}/status`);
}

export function cancelPaymentLink(context: SdkPaymentContext, linkId: string): Promise<{ id: string; status: "cancelled" }> {
  return context.request<{ id: string; status: "cancelled" }>(`/api/v1/payments/links/${encodeURIComponent(linkId)}/cancel`, { method: "POST", body: "{}" });
}

export function refundPayment(context: SdkPaymentContext, input: { paymentId: string; amountCents: number; idempotencyKey: string; reason?: string; method?: "mercadopago" | "cash"; occurrenceIds?: string[]; overrideReason?: string }): Promise<RefundSummary> {
  return context.request<RefundSummary>("/api/v1/payments/refund", { method: "POST", body: JSON.stringify(input), headers: { "Idempotency-Key": input.idempotencyKey } });
}
